/**
 * Sesión del panel.
 *
 * El arranque es una sola llamada: `GET /api/session` dice si hay sesión y de
 * paso entrega el token CSRF que necesitan todas las mutaciones —incluido el
 * POST de login—. Por eso `bootstrap()` corre antes del primer render y el
 * guard del router espera a que termine en vez de adivinar.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { setCsrfToken } from '@/api/client'
import * as panel from '@/api/panel'
import type { PanelUser } from '@/types/panel'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<PanelUser | null>(null)
  const checking = ref(false)
  /** `true` cuando ya se preguntó al backend, haya sesión o no. */
  const checked = ref(false)

  const isAuthenticated = computed(() => user.value !== null)
  const isAdmin = computed(() => user.value?.admin === true)

  async function bootstrap() {
    if (checking.value) return
    checking.value = true

    const result = await panel.fetchSession()
    if (result.successful) {
      user.value = result.data.authenticated ? result.data.user : null
      setCsrfToken(result.data.csrf_token)
    } else {
      user.value = null
    }

    checked.value = true
    checking.value = false
  }

  async function signIn(email: string, password: string) {
    const result = await panel.login(email, password)
    if (result.successful) {
      user.value = result.data.user
      // La sesión cambia de id al entrar, así que el token anterior ya no
      // vale: el backend devuelve el nuevo en la misma respuesta.
      setCsrfToken(result.data.csrf_token)
    }
    return result
  }

  async function signOut() {
    const result = await panel.logout()
    user.value = null
    if (result.successful) setCsrfToken(result.data.csrf_token)
  }

  /** Un 401 en cualquier llamada: la cookie caducó del lado del servidor. */
  function clear() {
    user.value = null
  }

  return { user, checking, checked, isAuthenticated, isAdmin, bootstrap, signIn, signOut, clear }
})
